import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ModalProps } from "../../../Shared/types/ModalProps";
import { GenericButton } from "../../../Shared/components/GenericButton";
import {
  GenericInput,
  GenericMultipleFileInput,
} from "../../../Shared/components/GenericInput";
import { GenericSelectInput } from "../../../Shared/components/GenericSelectInput";
import { OverlayLoader } from "../../../Shared/components/Overlay";
import { CreateDriverInput } from "../models/CreateDriverInput";
import { DriverService } from "../services/DriverService";
import { ToastResponse } from "../../../Shared/types/ToastProps";
import { Toast } from "../../../Shared/components/Toast";
import { IDriver } from "../models/Driver";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { UtilService } from "../../../Shared/Utils/UtilService";
import { IVehicle } from "../models/Vehicle";
import { VehicleService } from "../services/VehicleService";
import { PageOptionsDto } from "../../../Shared/dto/page.options.dto";
import { FileModel } from "../../../Shared/dto/create-file-input";
import { AuthService } from "../../Auth/services/AuthService";

type DriverFormProps = ModalProps & {
  driver?: IDriver;
  vehicle?: IVehicle;
};

export const DriverForm: React.FC<DriverFormProps> = ({
  onClose,
  driver,
  vehicle,
}) => {
  const { t } = useTranslation();
  const message = t('loading_message');
  const [showToast, setShowToast] = useState(false);
  const [toast, setToast] = useState<ToastResponse>();
  const [isLoading, setIsLoading] = useState(false);
  const [vehicleOptions, setVehicleOptions] = useState<any[]>([]);
  const companyId = AuthService.getLocalUser()?.user?.companies?.[0]?.id;
  const findVehicle = async () => {
    const pageOptions: PageOptionsDto = {
      page: 1,
      take: 50,
      query: "",
    };
    const response = await VehicleService.getAll(pageOptions);
    const vehicles = response?.data?.map((vehicle: any) => ({
      label: vehicle?.name ?? "Vehicle",
      value: vehicle?.id ?? "",
    }));
    if (vehicles.length > 0) {
      setVehicleOptions(vehicles);
    } else {
      onClose(false);
    }
  };

  useEffect(() => {
    if (!vehicle?.id) {
      findVehicle();
    }
  }, []);

  const [vehicleItem, setVehicleItem] = useState(driver?.vehicle?.id || vehicle?.id || '');
  const [vehicleError, setVehicleError] = useState("");
  const [country, setCountry] = useState(vehicle?.company.country || "");
  const [countryError, setCountryError] = useState("");
  const [firstName, setFirstName] = useState(driver?.firstName || "");
  const [firstNameError, setLastNameError] = useState("");
  const [lastName, setLastName] = useState(driver?.lastName || "");
  const [lastNameError, setFirstNameError] = useState("");
  const [middleName, setMiddleName] = useState(driver?.middleName || "");
  const [phoneNumber, setPhoneNumber] = useState(driver?.phoneNumber || "");
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [email, setEmail] = useState(driver?.email || "");
  const [emailError, setEmailError] = useState("");
  const [licenses, setLicenses] = useState<FileModel[]>(
    UtilService.attachmentsToFileModel(driver?.licenses) || []
  );
  const [licenseError, setLicenseError] = useState("");
  const [passports, setPassports] = useState<FileModel[]>(
    UtilService.attachmentsToFileModel(driver?.passports) || []
  );
  const [passportError, setPassportError] = useState("");

  const onVehicleItem = (val: string) => {
    setVehicleItem(val);
    setVehicleError(val ? "" : "");
  };

  const onCountry = (val: string) => {
    setCountry(val);
    setCountryError(val ? "" : "");
  };

  const onFirstName = (val: string) => {
    setFirstName(val);
    setFirstNameError(val.trim() === "" ? "" : "");
  };
  const onMiddleName = (val: string) => {
    setMiddleName(val);
  };
  const onLastName = (val: string) => {
    setLastName(val);
    setLastNameError(val.trim() === "" ? "" : "");
  };
  const onPhoneNumber = (val: string) => {
    setPhoneNumber(val);
    setPhoneNumberError(val.trim() === "" ? "" : "");
  };

  const onEmail = (val: string) => {
    setEmail(val);
    setEmailError(val.includes("@") ? "" : "");
  };

  const onFileLicenses = async (files: File[]) => {
    let licenseFiles = await Promise.all(
      files.map((file) => UtilService.onAttachFile(file, setLicenseError))
    );
    if (licenseFiles?.length > 0) {
      setLicenses(
        licenseFiles.filter((file): file is FileModel => file !== undefined)
      );
    }
  };

  const onFilePassports = async (files: File[]) => {
    let passportFiles = await Promise.all(
      files.map((file) => UtilService.onAttachFile(file, setPassportError))
    );
    if (passportFiles?.length > 0) {
      setPassports(
        passportFiles.filter((file): file is FileModel => file !== undefined)
      );
    }
  };

  const resetForm = () => {
    setVehicleItem("");
    setVehicleError("");
    setCountry("");
    setCountryError("");
    setFirstName("");
    setFirstNameError("");
    setMiddleName("");
    setLastName("");
    setLastNameError("");
    setPhoneNumber("");
    setPhoneNumberError("");
    setEmail("");
    setEmailError("");
    setLicenses([]);
    setLicenseError("");
  };

  const isFirstNameValid = firstName.trim() !== "";
  const isLastNameValid = lastName.trim() !== "";
  const isMiddleNameValid = middleName.trim();
  const isPhoneNumberValid = phoneNumber.trim() !== "";
  const isEmailValid = email.trim() !== "" && email.includes("@");

  const isFormValid =
    isFirstNameValid &&
    isLastNameValid &&
    isMiddleNameValid &&
    isPhoneNumberValid &&
    isEmailValid &&
    !firstNameError &&
    !lastNameError &&
    !phoneNumberError &&
    !emailError;

  const createAccountAction = async (e: React.FormEvent) => {
    e.preventDefault();
    const createDriverInput = new CreateDriverInput();
    if (driver?.id) {
      createDriverInput.id = driver.id;
    }

    createDriverInput.companyId =
      vehicle?.company?.id ?? companyId ?? undefined;
    createDriverInput.vehicleId =
      vehicleItem ?? driver?.vehicle?.id ?? undefined;
    createDriverInput.address = country.trim();
    createDriverInput.firstName = firstName.trim();
    createDriverInput.middleName = middleName.trim();
    createDriverInput.lastName = lastName.trim();
    createDriverInput.phoneNumber = phoneNumber.trim();
    createDriverInput.email = email.trim();
    createDriverInput.licenses = licenses;
    createDriverInput.passports = passports;
    try {
      setIsLoading(true);
      await DriverService.create(createDriverInput);
      resetForm();
      onClose(true);
    } catch (error: any) {
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const countryOptions = t('country_options', { returnObjects: true }) as { label: string, value: string }[];
  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
        <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg w-full max-w-md p-5 mt-10 mb-10 max-h-screen overflow-y-auto">
          <div className="flex justify-between items-center pb-3">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {driver?.id ? t('update_driver') :t('create_driver')}
            </h3>
            <button
              onClick={() => onClose(false)}
              className="text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="relative">
            <div className="w-full h-100">
              <form className="mt-3 mb-3" onSubmit={createAccountAction}>
               
                  <GenericSelectInput
                    label={t('select_truck_label')}
                    options={vehicle ? [{label:`${vehicle.plateNumber}: ${vehicle.name}`,value:vehicle.id}]: vehicleOptions}
                    value={vehicleItem}
                    onChange={onVehicleItem}
                    required={vehicle?.id != null ? true : false}
                    placeholder={t('select_truck_hint')}
                    error={vehicleError}
                    // disabled={vehicle && vehicle.id ? true : false}
                  />
               
                <div className="grid gap-6 mb-6 md:grid-cols-2">
                  <GenericSelectInput
                    containerClassName=""
                    label={t('country_label')}
                    options={countryOptions}
                    value={country}
                    onChange={onCountry}
                    required
                    placeholder={t('select_country')}
                    error={countryError}
                  />
                  <GenericInput
                    containerClassName=""
                    label={t('first_name_label')}
                    value={firstName}
                    onChange={onFirstName}
                    type="text"
                    error={firstNameError}
                    placeholder={t('first_name_hint')}
                    required={true}
                  />
                </div>
                <div className="grid gap-6 md:grid-cols-2 mb-6">
                  <GenericInput
                    containerClassName=""
                    label={t('middle_name_label')}
                    value={middleName}
                    onChange={onMiddleName}
                    type="text"
                    placeholder={t('middle_name_hint')}
                    required={false}
                  />
                  <GenericInput
                    containerClassName=""
                    label={t('last_name_label')}
                    value={lastName}
                    onChange={onLastName}
                    type="text"
                    error={lastNameError}
                    placeholder={t('last_name_hint')}
                    required={true}
                  />
                </div>
                <div className="grid gap-6 md:grid-cols-2 mb-6">
                  <GenericInput
                    containerClassName=""
                    label={t('phone_number_label')}
                    value={phoneNumber}
                    onChange={onPhoneNumber}
                    type="text"
                    error={phoneNumberError}
                    placeholder={t('phone_number_hint')}
                    required={true}
                  />

                  <GenericInput
                    containerClassName=""
                    label={t('email_label')}
                    value={email}
                    onChange={onEmail}
                    type="email"
                    error={emailError}
                    placeholder={t('email_hint')}
                    required={true}
                  />
                </div>
                <GenericMultipleFileInput
                  label={t('driver_license_label')}
                  onChange={onFileLicenses}
                  error={licenseError}
                  placeholder={t('driver_license_hint')}
                  required={false}
                />

                <GenericMultipleFileInput
                  label={t('driver_passport_label')}
                  onChange={onFilePassports}
                  error={passportError}
                  placeholder={t('driver_passport_hint')}
                  required={false}
                />
                <div className="mx-5">
                  <GenericButton type="submit" disabled={!isFormValid}>
                    {driver?.id ? t('update_driver') : t('create_driver')}
                  </GenericButton>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {showToast && (
        <Toast
          type={toast?.type}
          message={toast?.message ?? ""}
          onClose={() => setShowToast(false)}
        />
      )}
      {isLoading && <OverlayLoader message={message} />}
    </>
  );
};
